import docCookies from 'doc-cookies';
import { load } from 'recaptcha-v3';
import { xsfrToken } from '../alpine-functions';
import { RECAPTCHA_SITE_KEY } from '../misc/consts';

export default () => ({
  form: null,
  email: '',
  challange: '',
  sent: false,
  sending: false,
  failed: false,

  init() {
    this.form = this.$refs.form || this.$el.querySelector('form') || this.$el;
  },

  get action() {
    return this.form.action;
  },

  resetForm() {
    this.sent = false;
    this.failed = false;
    this.sending = false;
    this.form.reset();
  },

  isValid() {
    return this.form.reportValidity();
  },

  async getRecaptchaToken(action) {
    const siteKey =
      RECAPTCHA_SITE_KEY ||
      (typeof window !== 'undefined' ? window.RECAPTCHA_SITE_KEY : '');

    if (!siteKey) {
      throw new Error('Missing required parameters: sitekey');
    }

    const recaptcha = await load(siteKey);
    return await recaptcha.execute(action);
  },

  async send(event) {
    if (event?.currentTarget?.tagName === 'FORM') {
      this.form = event.currentTarget;
    }

    const dataLayer = window.dataLayer || [];
    const eventConfig = this.getFormEventConfig();

    if (this.sending || this.sent) {
      return;
    }

    if (!this.isValid()) {
      this.pushEvent(dataLayer, eventConfig, 'invalid');
      return false;
    }

    const data = new URLSearchParams(new FormData(this.form));

    this.sending = true;
    this.failed = false;
    this.pushEvent(dataLayer, eventConfig, 'attempt');

    await xsfrToken();
    const recaptchaAction = this.action.replace(/[^A-Za-z]/g, '');
    const token = await this.getRecaptchaToken(recaptchaAction);

    data.append('recaptcha_token', token);
    data.append('recaptcha_action', recaptchaAction);

    try {
      const response = await fetch(this.action, {
        body: data,
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          'X-XSRF-TOKEN': docCookies.getItem('XSRF-TOKEN'),
          Accept: 'application/json',
        },
        method: 'POST',
      });

      if (!response.ok) {
        const contentType = response.headers.get('content-type') || '';
        const payload = contentType.includes('application/json') ? await response.json() : null;
        const message = payload?.message || `HTTP ${response.status}`;
        throw new Error(message);
      }

      this.sent = true;
      this.failed = false;
      this.sending = false;
      this.pushEvent(dataLayer, eventConfig, 'success');
      setTimeout(() => {
        this.$dispatch('inzerce-close');
        setTimeout(() => {
          this.resetForm();
        }, 300);
      }, 3000);
    } catch (error) {
      console.error('Error:', error);
      this.sent = true;
      this.failed = true;
      this.sending = false;
      this.pushEvent(dataLayer, eventConfig, 'error', error.message);
    }
  },

  getFormEventConfig() {
    const eventSource = this.form.querySelector('[data-event-name]') || this.form;
    return this.getEventConfig(eventSource);
  },

  getEventConfig(element) {
    return {
      name: element.dataset.eventName || 'form_submit',
      category: element.dataset.eventCategory || 'form',
      label: element.dataset.eventLabel || '',
      value: element.dataset.eventValue || '0',
      ...this.getCustomEventData(element),
    };
  },

  getCustomEventData(element) {
    const dataset = element.dataset || {};

    const entries = Object.entries(dataset)
      .map(([key, value]) => {
        if (
          key.startsWith('event') &&
          !['eventName', 'eventCategory', 'eventLabel', 'eventValue'].includes(key)
        ) {
          const cleanKey = key
            .replace('event', '')
            .replace(/([A-Z])/g, '_$1')
            .toLowerCase()
            .replace(/^_/, '');

          return [cleanKey, value];
        }

        return null;
      })
      .filter(Boolean);

    return Object.fromEntries(entries);
  },

  pushEvent(dataLayer, config, status, errorMessage = null) {
    const { name, category, label, value, ...rest } = config;

    const eventData = {
      event: `${name}_${status}`,
      event_category: category,
      event_label: label,
      event_value: parseInt(value, 10) || 0,
      ...rest,
    };

    if (errorMessage) {
      eventData.error_message = errorMessage;
    }

    dataLayer.push(eventData);
  },
});
